// 比較ビュー（§10）。アクティブリーグの保存セットを共通単位で横並び比較。
// 各セットを §5 計算コア（概算モード=標本減）で評価し、結果をキャッシュ。
import { fromBase } from '../../currency/convert';
import { expectedHourlyBase, minHourlyBase, toDisplayUnit } from '../../model/derive';
import { expandSet } from '../../model/expand';
import type { EngineResult, FarmInput } from '../../core/types';
import type { FarmSet } from '../../store/schema';
import type { AppStore } from '../state';
import { el, mount } from '../dom';
import { fmtMoney, pct } from '../format';
import { ScatterChart } from '../components/scatter-chart';

export interface CompareCallbacks {
  getSets(): FarmSet[];
  onOpenInCalc(set: FarmSet): void;
  onDuplicate(id: string): void;
  onDelete(id: string): void;
}

type SortKey = 'name' | 'hourly' | 'trash' | 'bankHalf' | 'ev' | 'roi' | 'fstar' | 'time' | 'ruin';

interface Row {
  set: FarmSet;
  result: EngineResult;
  warnings: string[];
  hourly: number;
  trash: number;
}

export class CompareView {
  private commonUnit = 'divine';
  private sortKey: SortKey = 'hourly';
  private sortDir = -1;
  private rows: Row[] = [];
  private cache = new Map<string, { result: EngineResult; warnings: string[] }>();
  private scatter: ScatterChart | null = null;
  private unitInit = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly store: AppStore,
    private readonly computeOverview: (input: FarmInput) => Promise<EngineResult>,
    private readonly cb: CompareCallbacks,
  ) {}

  async show(): Promise<void> {
    const rt = this.store.rateTable;
    if (!rt) {
      mount(this.root, el('div', { class: 'panel' }, [el('div', { class: 'placeholder' }, '相場を取得しています…')]));
      return;
    }
    if (!this.unitInit) {
      this.commonUnit = this.store.currentSet.displayUnit;
      this.unitInit = true;
    }
    const sets = this.cb.getSets();
    if (sets.length === 0) {
      mount(this.root, el('div', { class: 'panel' }, [el('div', { class: 'placeholder' }, '保存済みセットがありません。計算ページで保存すると、ここで比較できます。')]));
      return;
    }
    mount(this.root, el('div', { class: 'panel' }, [el('div', { class: 'placeholder' }, '各セットを評価中…')]));

    this.rows = [];
    for (const set of sets) {
      const { input, warnings } = expandSet(set, rt);
      const key = `${set.id}|${rt.fetchedAt}|${JSON.stringify(input)}`;
      let cached = this.cache.get(key);
      if (!cached) {
        const result = await this.computeOverview(input);
        cached = { result, warnings };
        this.cache.set(key, cached);
      }
      this.rows.push({
        set,
        result: cached.result,
        warnings: cached.warnings,
        hourly: cached.result.valid ? expectedHourlyBase(cached.result, set.time) : -Infinity,
        trash: minHourlyBase(cached.result, set.time),
      });
    }
    this.render();
  }

  private sorted(): Row[] {
    const k = this.sortKey;
    const val = (r: Row): number | string => {
      switch (k) {
        case 'name': return r.set.name;
        case 'hourly': return r.hourly;
        case 'trash': return r.trash;
        case 'bankHalf': return r.result.bankHalf;
        case 'ev': return r.result.EV;
        case 'roi': return r.result.roi;
        case 'fstar': return r.result.fStar;
        case 'time': return r.set.time;
        case 'ruin': return r.result.ruinHalf;
      }
    };
    return [...this.rows].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (typeof va === 'string' || typeof vb === 'string') return String(va).localeCompare(String(vb)) * this.sortDir;
      return (va - vb) * this.sortDir;
    });
  }

  private setSort(k: SortKey): void {
    if (this.sortKey === k) this.sortDir *= -1;
    else { this.sortKey = k; this.sortDir = k === 'name' ? 1 : -1; }
    this.render();
  }

  private render(): void {
    const rt = this.store.rateTable!;
    const unit = this.commonUnit;
    const rows = this.sorted();

    const unitSel = el('select', {
      onchange: (e) => { this.commonUnit = (e.target as HTMLSelectElement).value; this.render(); },
    }) as HTMLSelectElement;
    for (const entry of rt.displayUnits()) unitSel.append(el('option', { value: entry.id, selected: entry.id === unit }, entry.text || entry.id));

    const th = (label: string, k: SortKey) =>
      el('th', { onclick: () => this.setSort(k), style: 'cursor:pointer;user-select:none' }, [
        document.createTextNode(label),
        this.sortKey === k ? el('span', { style: 'color:var(--gold)' }, this.sortDir < 0 ? ' ▾' : ' ▴') : null,
      ]);

    const anyWarn = rows.some((r) => r.warnings.length > 0);

    const body = el('tbody');
    for (const r of rows) {
      const d = toDisplayUnit(r.result, r.set.time, unit, rt);
      const invalid = !r.result.valid;
      const reachable = r.set.currentBankroll != null && r.result.valid && r.set.currentBankroll >= r.result.bankHalf;
      body.append(
        el('tr', {}, [
          el('td', {}, [
            el('b', {}, r.set.name || '（無名）'),
            r.set.notes?.trim() ? el('span', { class: 'note-mark', style: 'margin-left:6px', title: r.set.notes }, '📝') : null,
            r.warnings.length ? el('span', { class: 'badge badge-stale', style: 'margin-left:6px', title: r.warnings.join('\n') }, '要確認') : null,
          ]),
          el('td', { class: 'mono' }, invalid ? '—' : fmtMoney(d.expectedHourly, unit)),
          el('td', { class: 'mono' }, fmtMoney(d.minHourly, unit)),
          el('td', { class: 'mono' }, invalid ? '—' : fmtMoney(d.bankHalf, unit)),
          el('td', { class: 'mono' }, invalid ? (r.result.reason === 'rate' ? '相場未取得' : 'EV≤0') : fmtMoney(d.EV, unit)),
          el('td', { class: 'mono' }, invalid ? '—' : `${pct(d.roi)} / ${pct(d.fStar)}`),
          el('td', { class: 'mono' }, `${r.set.time}s`),
          el('td', { class: 'mono' }, invalid ? '—' : pct(r.result.ruinHalf)),
          el('td', {}, reachable ? [el('span', { class: 'badge badge-auto' }, '届く')] : ''),
          el('td', {}, [
            el('button', { class: 'iconbtn', title: '計算ページで開く', onclick: () => this.cb.onOpenInCalc(r.set) }, '↗'),
            el('button', { class: 'iconbtn', title: '複製', onclick: () => { this.cb.onDuplicate(r.set.id); void this.show(); } }, '⎘'),
            el('button', { class: 'iconbtn danger', title: '削除', onclick: () => { this.cb.onDelete(r.set.id); void this.show(); } }, '×'),
          ]),
        ]),
      );
    }

    const table = el('table', { class: 'cmp' }, [
      el('thead', {}, [
        el('tr', {}, [
          th('セット', 'name'),
          th('期待時給', 'hourly'),
          th('最低時給', 'trash'),
          th('軍資金(ハーフ)', 'bankHalf'),
          th('EV/周', 'ev'),
          th('ROI / f*', 'roi'),
          th('時間', 'time'),
          th('破産%', 'ruin'),
          el('th', {}, '到達'),
          el('th', {}, ''),
        ]),
      ]),
      body,
    ]);

    const canvas = el('canvas') as HTMLCanvasElement;
    mount(
      this.root,
      el('div', { class: 'panel' }, [
        el('div', { class: 'panel-head' }, [
          el('h2', {}, 'セット比較'),
          el('div', { class: 'field', style: 'flex-direction:row;align-items:center;gap:8px' }, [el('label', {}, '共通単位'), unitSel]),
        ]),
        anyWarn ? el('p', { class: 'note-soft', style: 'cursor:default' }, '一部のセットはレート未取得の通貨を参照しています（行の「要確認」）。') : null,
        el('div', { class: 'tablewrap' }, [table]),
        el('div', { class: 'chartwrap', style: 'height:320px;margin-top:1.5rem' }, [canvas]),
      ]),
    );

    this.scatter?.destroy();
    this.scatter = new ScatterChart(canvas);
    this.scatter.update(
      rows
        .filter((r) => r.result.valid && r.result.bankHalf > 0)
        .map((r) => ({
          name: r.set.name || '（無名）',
          bankHalf: fromBase(r.result.bankHalf, unit, rt) ?? 0,
          expectedHourly: fromBase(expectedHourlyBase(r.result, r.set.time), unit, rt) ?? 0,
          reachable: r.set.currentBankroll != null && r.set.currentBankroll >= r.result.bankHalf,
        })),
      unit,
    );
  }
}
